import { useCallback, useEffect, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { EVO_OVERLAY_DEFAULT_WIDTH } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/evoOverlayPosition';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import CloseIcon from '@/app/components/search/components/Modal/CloseIcon';
import { modalCloseButton } from '@/app/components/search/components/Modal/closeButtonStyles';
import { focusRing, hideScrollbar } from '@/app/components/search/styles/mixins';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [role="checkbox"]:not([disabled]), [role="radio"]:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement
  );
}

// #region EvoOverlayFilterModal Styles

const PillOverlayContainer = styled.div`
  /* MPH-474: pre-position fallback only — setEvoOverlayPosition applies the per-filter Figma width inline */
  width: ${EVO_OVERLAY_DEFAULT_WIDTH}px;
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
  overflow: hidden;

  :focus-visible {
    ${focusRing}
  }
`;

const Header = styled.div<{ $hasScrollShadow: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  min-height: 40px;
  /* MPH-919: only the content scrolls — the header keeps its Figma height when the popover is
     capped to the available space on short viewports. */
  flex-shrink: 0;
  padding: 12px 8px 12px 24px;
  z-index: 1;
  transition: box-shadow 0.2s ease-in-out;

  ${({ $hasScrollShadow }) =>
    $hasScrollShadow &&
    css`
      box-shadow: ${({ theme }) => theme.kallisto.shadow.smallDownShadow};
    `}
`;

const CloseButton = styled.button`
  ${modalCloseButton}
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: min(484px, 80vh);
  padding: 0 24px 16px;
  ${hideScrollbar};
`;

const Footer = styled.div<{ $hasScrollShadow: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  column-gap: 24px;
  min-height: 56px;
  /* MPH-919: the Reset/Apply surface must never be squeezed or pushed out when the popover is
     capped to the available space on short viewports. */
  flex-shrink: 0;
  padding: 16px;
  background-color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  z-index: 1;

  ${({ $hasScrollShadow }) =>
    $hasScrollShadow &&
    css`
      border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
      box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumTopShadow};
    `}
`;

const buttonFont = css`
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.medium};
`;

const ResetButton = styled.button<{ $enabled: boolean }>`
  ${buttonFont};
  padding: 0;
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
  cursor: default;

  ${({ $enabled }) =>
    $enabled &&
    css`
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }
    `}

  &:focus-visible {
    ${focusRing}
  }
`;

const ApplyButton = styled.button`
  ${buttonFont};
  min-height: 48px;
  padding: 12px 16px;
  border: 0;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
  color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryHover};
  }

  &:focus-visible {
    ${focusRing}
  }
`;

// #endregion EvoOverlayFilterModal Styles

type EvoOverlayFilterModalProps = {
  overlayRef: React.RefObject<HTMLDivElement | null>;
  control: string;
  modalTitle: string;
  closeModal: () => void;
  applyFilter: () => void;
  hasActiveFilterSelectedOptions: () => boolean;
  clearFilter: () => void;
  renderActiveFilterModalContent: () => React.ReactNode;
};

export function EvoOverlayFilterModal(props: EvoOverlayFilterModalProps) {
  const {
    overlayRef,
    control,
    modalTitle,
    closeModal,
    applyFilter,
    hasActiveFilterSelectedOptions,
    clearFilter,
    renderActiveFilterModalContent,
  } = props;
  const { t } = useTranslation();
  const [hasScrolledContent, setHasScrolledContent] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // The footer divider/shadow only shows when the content actually overflows (Figma);
  // ResizeObserver keeps it in sync if the rendered filter content changes size.
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;
    const update = () => setHasScrollableContent(element.scrollHeight > element.clientHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Move focus into the popover on open and restore it to the trigger pill on close.
  // The popover is portaled to the end of the change-flight dialog, so without this the
  // Tab order skips it entirely and the checkboxes are unreachable by keyboard.
  useEffect(() => {
    const container = overlayRef.current;
    const trigger = document.activeElement as HTMLElement | null;
    container?.focus();
    return () => {
      if (container?.contains(document.activeElement)) {
        trigger?.focus?.();
      }
    };
  }, [overlayRef]);

  // Trap Tab within the popover and close it on Escape. Listening on window in the capture
  // phase runs before the parent Radix dialog's document-capture Escape handler, so calling
  // preventDefault/stopPropagation closes the popover only and leaves the dialog open (Radix
  // dismisses only when the event is not already defaultPrevented).
  useEffect(() => {
    const container = overlayRef.current;
    if (!container) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeModal();
        return;
      }
      if (event.key === 'Tab' && container.contains(document.activeElement)) {
        const focusables = getFocusableElements(container);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [overlayRef, closeModal]);

  const onClickOutsideRef = useOnclickOutside(
    () => {
      closeModal();
    },
    { ignoreClass: [`Pill-${control}`] }
  );

  const setOverlayRef = useCallback(
    (element: HTMLDivElement | null) => {
      overlayRef.current = element;
      onClickOutsideRef(element);
    },
    [overlayRef, onClickOutsideRef]
  );

  const hasSelection = hasActiveFilterSelectedOptions();

  return (
    <PillOverlayContainer
      ref={setOverlayRef}
      className={control || ''}
      tabIndex={0}
      role="dialog"
      aria-label={modalTitle}
    >
      <Header $hasScrollShadow={hasScrolledContent}>
        <Text variant="Title4" color="textPrimary">
          {modalTitle}
        </Text>
        <CloseButton type="button" aria-label={t('vacation.oss.cards.map.close')} onClick={closeModal}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <ContentContainer ref={contentRef} onScroll={(event) => setHasScrolledContent(event.currentTarget.scrollTop > 0)}>
        {renderActiveFilterModalContent()}
      </ContentContainer>
      {control !== 'sort' ? (
        <Footer $hasScrollShadow={hasScrollableContent}>
          <ResetButton
            type="button"
            $enabled={hasSelection}
            disabled={!hasSelection}
            onClick={hasSelection ? clearFilter : undefined}
          >
            {t('vacation.oss.change_transport.filters.reset')}
          </ResetButton>
          <ApplyButton type="button" onClick={applyFilter}>
            {t('vacation.oss.filters.apply')}
          </ApplyButton>
        </Footer>
      ) : null}
    </PillOverlayContainer>
  );
}
